import { ReactNode, useMemo, useState } from 'react';
import { Table, Thead, Tbody, Tr, Th, Td, Spinner, Box, Input, Stack } from '@chakra-ui/react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  SortingState,
  ColumnDef
} from '@tanstack/react-table';
import IconComponent from '../IconComponent/IconComponent';

export type DataTableProps<Data extends object> = {
  data: Data[];
  columns: ColumnDef<Data, any>[];
  extraThead?: ReactNode;
  isLoading: boolean;
  disableFilters?: boolean;
  includeIdInSearch?: boolean;
  filterComponent?: ReactNode;
  additionalFilters?: ReactNode;
  hideSearch?: boolean;
  customSearchAction?: (value: string) => void;
  striped?: boolean;
  noVerticalLineIndices?: number[];
  searchInputWidth?: string;
};

export function DataTable<Data extends object>({
  data,
  columns,
  extraThead,
  isLoading,
  disableFilters,
  filterComponent,
  hideSearch = false,
  includeIdInSearch = false,
  customSearchAction,
  striped = true,
  noVerticalLineIndices = [],
  additionalFilters,
  searchInputWidth = '100%'
}: DataTableProps<Data>) {
  const [searchPhrase, setSearchPhrase] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);

  const filteredData = useMemo(() => {
    if (customSearchAction || !searchPhrase) return data;
    return data.filter((row) =>
      Object.entries(row).some(([key, value]) => {
        if (!includeIdInSearch &&key === 'id') return false;
        if (typeof value === 'object' && value !== null) {
          return Object.values(value).some((sub) =>
            String(sub).toLowerCase().includes(searchPhrase.toLowerCase())
          );
        }
        return String(value).toLowerCase().includes(searchPhrase.toLowerCase());
      })
    );
  }, [data, searchPhrase, customSearchAction]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { sorting },
    onSortingChange: setSorting
  });

  return (
    <Box width="100%">
      {!disableFilters && (
        <Stack direction={{ base: 'column', md: 'row' }} spacing={4} mb={4}>
          {!hideSearch && (
            <Input
              value={searchPhrase}
              onChange={(e) => setSearchPhrase(e.target.value)}
              placeholder="Szukaj..."
              width={searchInputWidth}
            />
          )}
          {filterComponent}
          {additionalFilters}
        </Stack>
      )}

      <Box overflowX="auto" width="100%">
        <Table variant="simple" size="sm" className="min-w-full table-auto">
          <Thead>
            {extraThead}
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id} className="bg-grayLight-25 border border-gray-300">
                {headerGroup.headers.map((header, index) => {
                  const meta = header.column.columnDef.meta as { isNumeric?: boolean } | undefined;
                  return (
                    <Th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      isNumeric={meta?.isNumeric}
                      style={{
                        height: '60px',
                        padding: '5px 5px 5px 12px',
                        verticalAlign: 'middle',
                        position: 'relative',
                        textTransform: 'none',
                        width: header.column.getSize()
                      }}
                      className="bg-grayLight-25 border border-gray-300 cursor-pointer"
                      borderRight={
                        noVerticalLineIndices.includes(index)
                          ? 'none'
                          : '1px solid var(--chakra-colors-gray-200)'
                      }>
                      <div
                        className="flex items-center"
                        style={{
                          height: '100%',
                          width: header.column.getSize()
                        }}>
                        <span className="text-xs font-medium text-grayLight-600">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        <span className="pl-4">
                          {header.column.getIsSorted() ? (
                            header.column.getIsSorted() === 'desc' ? (
                              <IconComponent iconName="arrow" />
                            ) : (
                              <IconComponent iconName="arrow" className="rotate-180" />
                            )
                          ) : null}
                        </span>
                      </div>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>

          <Tbody>
            {isLoading ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center">
                  <Spinner />
                </Td>
              </Tr>
            ) : (
              table.getRowModel().rows.map((row, rowIndex) => (
                <Tr
                  key={row.id}
                  className={striped && rowIndex % 2 === 1 ? 'bg-brand-50' : undefined}>
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <Td
                      key={cell.id}
                      borderRight={
                        noVerticalLineIndices.includes(cellIndex)
                          ? 'none'
                          : '1px solid var(--chakra-colors-gray-100)'
                      }
                      whiteSpace="nowrap">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}
